import React, { useMemo } from "react";
import {
  Checkbox,
  FormControl,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import { useTranslation } from "../i18n/useTranslation";

const SearchAndFilters = ({
  query,
  onQueryChange,
  typeFilter,
  typeOptions = [],
  onTypeFilterChange,
  routeOptions,
  selectedRoutes,
  onRouteChange,
}) => {
  const { t } = useTranslation();

  const selectedRouteLabels = useMemo(() => {
    if (!selectedRoutes.length) {
      return t("map.allRoutes");
    }

    return routeOptions
      .filter((option) => selectedRoutes.includes(option.value))
      .map((option) => option.label)
      .join(", ");
  }, [routeOptions, selectedRoutes, t]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        background: "linear-gradient(135deg, rgba(21,101,216,0.1), rgba(118,75,162,0.15))",
        border: "1px solid rgba(21, 101, 216, 0.15)",
      }}
    >
      <Stack spacing={2.5}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <TuneIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t("search.heading")}
          </Typography>
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
          <Stack spacing={0.5} sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t("search.queryLabel")}
            </Typography>
            <TextField
              fullWidth
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              placeholder={t("search.placeholder")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          <FormControl sx={{ minWidth: { xs: "100%", md: 240 } }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
              {t("search.routeLabel")}
            </Typography>
            <Select
              multiple
              displayEmpty
              value={selectedRoutes}
              onChange={(event) => {
                const value = event.target.value;
                if (Array.isArray(value)) {
                  if (value.includes("__all__")) {
                    onRouteChange([]);
                  } else {
                    onRouteChange(value);
                  }
                } else {
                  onRouteChange([]);
                }
              }}
              renderValue={() => selectedRouteLabels}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="__all__">
                <Typography variant="body2" color="primary">
                  {t("map.allRoutes")}
                </Typography>
              </MenuItem>
              {routeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={selectedRoutes.includes(option.value)} />
                  <Typography variant="body2">{option.label}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <ToggleButtonGroup
          value={typeFilter}
          exclusive
          onChange={(_, value) => value && onTypeFilterChange(value)}
          color="primary"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            "& .MuiToggleButton-root": {
              flex: 1,
              minWidth: 140,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 600,
            },
          }}
        >
          <ToggleButton value="all">{t("search.type.all")}</ToggleButton>
          {typeOptions.map((option) => (
            <ToggleButton key={option.value} value={option.value}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

      </Stack>
    </Paper>
  );
};

export default SearchAndFilters;
